import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  AlertTitle,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import axios from 'axios';
import { createPart, getPartTypes } from '../../services/partService';
import { getLocations } from '../../services/locationService';
import { getSuppliers } from '../../services/supplierService';
import { toast } from 'react-toastify';
import PartLabelPrintDialog from '../../components/PartLabelPrintDialog';

const NewPart = () => {
  const navigate = useNavigate();

  const [partTypes, setPartTypes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // State for print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [createdPart, setCreatedPart] = useState(null);

  // State for early print notification
  const [showEarlyPrintNotification, setShowEarlyPrintNotification] = useState(false);

  // State for part number validation
  const [partNumberError, setPartNumberError] = useState(null);
  const [checkingPartNumber, setCheckingPartNumber] = useState(false);
  const [existingPart, setExistingPart] = useState(null);

  const [part, setPart] = useState({
    internal_part_number: '',
    description: '',
    part_type_id: '',
    is_special_order: false,
    is_returnable: true,
    can_bundle: false,
    cost: '',
    reorder_point: '',
    preferred_supplier_id: ''
  });

  // State for initial inventory
  const [addInitialInventory, setAddInitialInventory] = useState(false);
  const [initialInventory, setInitialInventory] = useState({
    location_id: '',
    quantity: ''
  });
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch part types using the service
        const partTypesData = await getPartTypes();
        // Ensure partTypes is an array
        setPartTypes(Array.isArray(partTypesData) ? partTypesData : []);

        // Fetch suppliers using the service
        const suppliersData = await getSuppliers();
        // Ensure suppliers is an array
        setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);

        // Fetch locations for initial inventory
        const locationsData = await getLocations();
        // Ensure locations is an array
        setLocations(Array.isArray(locationsData) ? locationsData : []);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setPart({
      ...part,
      [name]: newValue
    });

    // Check if we should show early print notification
    if (name === 'is_special_order') {
      const updatedPart = { ...part, [name]: newValue };
      if (updatedPart.internal_part_number && updatedPart.is_special_order) {
        setShowEarlyPrintNotification(true);
      } else {
        setShowEarlyPrintNotification(false);
      }
    }

    // Clear part number error and existing part when user starts typing
    if (name === 'internal_part_number') {
      setPartNumberError(null);
      setExistingPart(null);
    }
  };

  const handleInventoryChange = (e) => {
    const { name, value } = e.target;
    setInitialInventory({
      ...initialInventory,
      [name]: value
    });
  };

  // Check if part number already exists
  const checkPartNumberExists = async (partNumber) => {
    if (!partNumber.trim()) {
      setPartNumberError(null);
      setExistingPart(null);
      return;
    }

    setCheckingPartNumber(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/parts?search=${encodeURIComponent(partNumber)}`, {
        headers: {
          'x-auth-token': token
        }
      });

      // Check if any part has exact match for internal_part_number
      const exactMatch = response.data.find(p =>
        p.internal_part_number.toLowerCase() === partNumber.toLowerCase()
      );

      if (exactMatch) {
        setExistingPart(exactMatch);
        if (exactMatch.is_special_order) {
          setPartNumberError('This special order part already exists. You can print a label for it below.');
        } else {
          setPartNumberError('A part with this part number already exists');
        }
      } else {
        setPartNumberError(null);
        setExistingPart(null);
      }
    } catch (err) {
      console.error('Error checking part number:', err);
      // Don't show error to user for this check, just log it
    } finally {
      setCheckingPartNumber(false);
    }
  };

  // Handle part number field blur to show early print notification and validate
  const handlePartNumberBlur = () => {
    if (part.internal_part_number) {
      // Check for duplicates
      checkPartNumberExists(part.internal_part_number);

      // Show print notification if special order
      if (part.is_special_order) {
        setShowEarlyPrintNotification(true);
      }
    } else {
      setPartNumberError(null);
      setShowEarlyPrintNotification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!part.internal_part_number || !part.description || !part.part_type_id) {
      setError('Please fill in all required fields');
      return;
    }

    // Check for part number error - but allow special order parts to print labels
    if (partNumberError && !(existingPart && existingPart.is_special_order)) {
      setError(partNumberError);
      return;
    }

    // If it's an existing special order part, just show print dialog
    if (existingPart && existingPart.is_special_order) {
      handlePrintExistingPart();
      return;
    }

    // Validate initial inventory if enabled
    if (addInitialInventory && (!initialInventory.location_id || !initialInventory.quantity)) {
      setError('Please fill in all initial inventory fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Create part
      const newPart = await createPart(part);

      // Add initial inventory if enabled
      if (addInitialInventory && newPart.id) {
        try {
          const token = localStorage.getItem('token');
          await axios.post('/api/inventory/adjust', {
            part_id: newPart.id,
            location_id: initialInventory.location_id,
            transaction_type: 'receive',
            quantity_change: parseInt(initialInventory.quantity),
            notes: 'Initial inventory'
          }, {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          });
        } catch (inventoryErr) {
          console.error('Error adding initial inventory:', inventoryErr);
          toast.error('Part created but failed to add initial inventory');
        }
      }

      toast.success('Part created successfully');

      // Check if this is a special order part and show print dialog
      if (newPart.is_special_order) {
        setCreatedPart(newPart);
        setShowPrintDialog(true);
        setSubmitting(false);
      } else {
        // Redirect to part details for non-special order parts
        navigate(`/inventory/${newPart.id}`);
      }
    } catch (err) {
      console.error('Error creating part:', err);
      setError(err.message || 'Failed to create part');
      setSubmitting(false);
    }
  };

  // Handle print dialog close
  const handlePrintDialogClose = () => {
    setShowPrintDialog(false);
    setCreatedPart(null);
    // Navigate to part details after dialog closes
    if (createdPart) {
      navigate(`/inventory/${createdPart.id}`);
    }
  };

  // Handle printing label for existing part
  const handlePrintExistingPart = () => {
    if (existingPart) {
      setCreatedPart(existingPart);
      setShowPrintDialog(true);
    }
  };

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1">
          Add New Part
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error</AlertTitle>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Part Type</InputLabel>
                    <Select
                      name="part_type_id"
                      value={part.part_type_id}
                      onChange={handleChange}
                      label="Part Type"
                      sx={{ minWidth: '200px' }}
                    >
                      <MenuItem value="">
                        <em>Select Part Type</em>
                      </MenuItem>
                      {partTypes.map(type => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Part Number"
                    name="internal_part_number"
                    value={part.internal_part_number}
                    onChange={handleChange}
                    onBlur={handlePartNumberBlur}
                    error={!!partNumberError}
                    helperText={partNumberError || "Unique internal identifier for this part"}
                    InputProps={{
                      endAdornment: checkingPartNumber && (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    rows={2}
                    label="Description"
                    name="description"
                    value={part.description}
                    onChange={handleChange}
                    helperText="Detailed description of the part"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Early Print Notification */}
            {showEarlyPrintNotification && !existingPart && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  📄 This is a special order part. You'll be prompted to print a label after saving.
                </Typography>
              </Alert>
            )}

            {/* Existing Special Order Part Notification */}
            {existingPart && existingPart.is_special_order && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Special Order Part Found: {existingPart.internal_part_number}
                    </Typography>
                    <Typography variant="body2">
                      {existingPart.description}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handlePrintExistingPart}
                    sx={{ ml: 2 }}
                  >
                    Print Label
                  </Button>
                </Box>
              </Alert>
            )}

            <Divider />

            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Inventory Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Preferred Supplier</InputLabel>
                    <Select
                      name="preferred_supplier_id"
                      value={part.preferred_supplier_id}
                      onChange={handleChange}
                      label="Preferred Supplier"
                      sx={{ minWidth: '200px' }}
                    >
                      <MenuItem value="">
                        <em>Select Preferred Supplier (Optional)</em>
                      </MenuItem>
                      {suppliers.map(supplier => (
                        <MenuItem key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cost"
                    name="cost"
                    value={part.cost}
                    onChange={handleChange}
                    inputProps={{ step: "0.01", min: "0" }}
                    helperText="Unit cost of the part"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Reorder Point"
                    name="reorder_point"
                    value={part.reorder_point}
                    onChange={handleChange}
                    inputProps={{ min: "0" }}
                    helperText="Minimum quantity before reordering"
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Part Properties
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={part.is_special_order}
                        onChange={handleChange}
                        name="is_special_order"
                      />
                    }
                    label="Special Order Item"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                    Check if this part typically needs to be specially ordered
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={part.is_returnable}
                        onChange={handleChange}
                        name="is_returnable"
                      />
                    }
                    label="Returnable"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                    Check if this part can be returned to the supplier
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={part.can_bundle}
                        onChange={handleChange}
                        name="can_bundle"
                      />
                    }
                    label="Can Bundle"
                  />
                  <Typography variant="caption" display="block" sx={{ ml: 4 }}>
                    Check if this part can be bundled with other parts
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            <Box sx={{ my: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Initial Inventory
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={addInitialInventory}
                    onChange={(e) => setAddInitialInventory(e.target.checked)}
                  />
                }
                label="Add Initial Inventory"
              />
              <Typography variant="caption" display="block" sx={{ ml: 4, mb: 2 }}>
                Set up initial inventory quantity and location for this part
              </Typography>

              {addInitialInventory && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Location</InputLabel>
                        <Select
                          name="location_id"
                          value={initialInventory.location_id}
                          onChange={handleInventoryChange}
                          label="Location"
                        >
                          <MenuItem value="">
                            <em>Select Location</em>
                          </MenuItem>
                          {locations.map(location => (
                            <MenuItem key={location.id} value={location.id}>
                              {location.warehouse.name} - {location.rack}-{location.shelf}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        required
                        type="number"
                        label="Quantity"
                        name="quantity"
                        value={initialInventory.quantity}
                        onChange={handleInventoryChange}
                        inputProps={{ min: "1" }}
                        helperText="Initial quantity to add to inventory"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                component={RouterLink}
                to="/inventory"
                variant="outlined"
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={submitting && <CircularProgress size={20} />}
              >
                {submitting ? 'Saving...' :
                 (existingPart && existingPart.is_special_order) ? 'Print Label' : 'Save Part'}
              </Button>
            </Box>
          </form>
        </Paper>
      )}

      {/* Part Label Print Dialog */}
      <PartLabelPrintDialog
        open={showPrintDialog}
        onClose={handlePrintDialogClose}
        part={createdPart}
      />
    </Container>
  );
};

export default NewPart;
